import { FC, ReactElement } from 'react';
import { Card, Col, Row, Stack } from 'react-bootstrap';
import { CameraVideo, CameraVideoOff, Mic, MicMute } from 'react-bootstrap-icons';
import Button from 'react-bootstrap/Button';
import { Call, ParticipantView, StreamVideoParticipant, useCall, useCallStateHooks } from '@stream-io/video-react-sdk';
import CopyValueBox from '../CopyValueBox';
import i18next from 'i18next';

type CallWaitingRoomProps = {
  roomCode: string;
  onLeave: () => void;
};

const CallWaitingRoom: FC<CallWaitingRoomProps> = ({ roomCode, onLeave }: CallWaitingRoomProps): ReactElement => {
  const call: Call | undefined = useCall();
  const { useLocalParticipant, useCameraState, useMicrophoneState } = useCallStateHooks();
  const localParticipant: StreamVideoParticipant | undefined = useLocalParticipant();
  const cameraState = useCameraState();
  const microphoneState = useMicrophoneState();

  const isCameraOn: boolean = cameraState.camera.enabled;
  const isMicOn: boolean = microphoneState.microphone.enabled;

  const handleToggleCamera = async (): Promise<void> => {
    if (!call) return;

    if (isCameraOn) {
      await call.camera.disable().catch(() => undefined);
    } else {
      await call.camera.enable().catch(() => undefined);
    }
  };

  const handleToggleMicrophone = async (): Promise<void> => {
    if (!call) return;

    if (isMicOn) {
      await call.microphone.disable().catch(() => undefined);
    } else {
      await call.microphone.enable().catch(() => undefined);
    }
  };

  return (
    <Card
      className="border-0 overflow-hidden shadow-lg w-100"
      style={{
        maxWidth: '1120px',
        minHeight: 'clamp(460px, 55vh, 680px)',
        borderRadius: '1.25rem',
        backgroundColor: '#31424d',
      }}
    >
      <Card.Body className="p-0 d-flex flex-column h-100">
        <Row className="g-0 flex-grow-1 h-100">
          <Col xs={12} lg={8} className="d-flex">
            <div
              className="position-relative w-100 overflow-hidden flex-grow-1"
              style={{
                backgroundColor: '#1c2b3f',
                borderRadius: '1.25rem 0 0 1.25rem',
                minHeight: '280px',
              }}
            >
              {localParticipant && isCameraOn ? (
                <div className="w-100 h-100">
                  <ParticipantView
                    className="w-100 h-100 text-white"
                    participant={localParticipant}
                    muteAudio
                  />
                </div>
              ) : (
                <div className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center text-center px-4 text-white">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle mb-3"
                    style={{
                      width: '72px',
                      height: '72px',
                      backgroundColor: 'rgba(255, 255, 255, 0.12)',
                    }}
                  >
                    <CameraVideoOff size={32} />
                  </div>

                  <div className="fw-semibold mb-2">
                    {i18next.t('callPreview.cameraDeactivated')}
                  </div>

                  <div className="small opacity-75">
                    {i18next.t('callPreview.joinWithoutCamera')}
                  </div>
                </div>
              )}

              <div
                className="position-absolute start-50 translate-middle-x px-3 py-2"
                style={{
                  bottom: '16px',
                  backgroundColor: 'rgba(32, 43, 52, 0.88)',
                  borderRadius: '999px',
                  backdropFilter: 'blur(8px)',
                }}
              >
                <Stack direction="horizontal" gap={2}>
                  <Button
                    variant={isCameraOn ? 'light' : 'danger'}
                    className="d-inline-flex align-items-center justify-content-center rounded-circle border-0"
                    style={{ width: '48px', height: '48px' }}
                    onClick={() => void handleToggleCamera()}
                    aria-label={isCameraOn ? i18next.t('callPreview.deactivateCamera') : i18next.t('callPreview.activateCamera')}
                  >
                    {isCameraOn ? <CameraVideo size={18} /> : <CameraVideoOff size={18} />}
                  </Button>

                  <Button
                    variant={isMicOn ? 'light' : 'danger'}
                    className="d-inline-flex align-items-center justify-content-center rounded-circle border-0"
                    style={{ width: '48px', height: '48px' }}
                    onClick={() => void handleToggleMicrophone()}
                    aria-label={isMicOn ? i18next.t('callPreview.deactivateMicrophone') : i18next.t('callPreview.activateMicrophone')}
                  >
                    {isMicOn ? <Mic size={18} /> : <MicMute size={18} />}
                  </Button>
                </Stack>
              </div>
            </div>
          </Col>

          <Col xs={12} lg={4}>
            <div
              className="h-100 d-flex flex-column"
              style={{
                background: 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)',
              }}
            >
              <div className="p-4">
                <div className="text-white fw-bold mb-2" style={{ fontSize: '1.75rem', lineHeight: 1.1 }}>
                  {i18next.t('callPreview.title')}
                </div>

                <div className="mb-4" style={{ color: 'rgba(255, 255, 255, 0.78)' }}>
                  {i18next.t('callPreview.subTitle')}
                </div>

                <div className="mb-4">
                  <div
                    className="text-uppercase fw-semibold mb-2"
                    style={{
                      color: 'rgba(255, 255, 255, 0.65)',
                      fontSize: '0.8rem',
                      letterSpacing: '0.04rem',
                    }}
                  >
                    {i18next.t('callPreview.roomCode')}
                  </div>

                  <CopyValueBox value={roomCode} valueCopy={window.location.href} />
                </div>
              </div>

              <div
                className="mt-auto px-4 py-4"
                style={{
                  backgroundColor: 'rgba(17, 28, 37, 0.45)',
                  borderTop: '1px solid rgba(255, 255, 255, 0.06)',
                }}
              >
                <div
                  className="text-center mb-3"
                  style={{ color: 'rgba(255,255,255,0.88)', fontSize: '0.95rem' }}
                >
                  {i18next.t('callPreview.waitingInfoText')}
                </div>

                <div className="d-grid">
                  <Button
                    variant="outline-light"
                    onClick={onLeave}
                    className="fw-semibold"
                    style={{ minHeight: '48px' }}
                  >
                    {i18next.t('callPreview.leaveWaitingRoom')}
                  </Button>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>

  );
};

export default CallWaitingRoom;